import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { SparkleGlyph } from "./SparkleGlyph";
import type { MSHatData } from "../../types";
import type { FacetDefinition } from "../../../components/FacetInfoModal";
import {
  HAT_HIGH_INTEREST_THRESHOLD,
  HAT_LOW_INTEREST_THRESHOLD,
  HAT_MIN_VALID_SCORE,
} from "../../../constants/interestThresholds";

/**
 * A hat's Core types and Work Values, scored against the climber, and the two
 * lists that draw them.
 *
 * Split out of BackCenterPanel so a second surface can show the same thing in
 * a different shape. The hat card stacks Core over Work Values inside a fixed
 * 368x569 face; the Climb Plan review puts them side by side across a 992px
 * panel. Only the arrangement differs — the tiers, the purple Fit tags and the
 * interest bars are the same reading of the same data, and a second copy of
 * this scoring would be a second answer to "does this hat fit me".
 */

export type InterestTier = "high" | "some" | "low";

export const RIASEC_SCORE_TO_FACET: Record<string, string> = {
  realistic: "RIASEC:R",
  investigative: "RIASEC:I",
  artistic: "RIASEC:A",
  social: "RIASEC:S",
  enterprising: "RIASEC:E",
  conventional: "RIASEC:C",
};

export const WORK_VALUE_SCORE_TO_FACET: Record<string, string> = {
  achievement: "Accomplishing Goals",
  independence: "Being Independent",
  recognition: "Getting Recognition",
  relationships: "Building Relationships",
  support: "Trust & Support",
  workingConditions: "Stability & Security",
};

// Object key order is the canonical RIASEC order: Doer, Analyzer, Creator,
// Helper, Persuader, Organizer.
export const RIASEC_KEYS = Object.keys(RIASEC_SCORE_TO_FACET);

/**
 * The facet codes in canonical RIASEC order - Doer, Analyzer, Creator, Helper,
 * Persuader, Organizer.
 *
 * Every surface that lists Core types draws them in this order, so a climber
 * reading their own results beside a Hat's is comparing two readings of one
 * list rather than two differently-ordered lists.
 */
export const RIASEC_FACET_ORDER = RIASEC_KEYS.map(
  (key) => RIASEC_SCORE_TO_FACET[key],
);
export const WORK_VALUE_KEYS = Object.keys(WORK_VALUE_SCORE_TO_FACET);

export const getTier = (score: number): InterestTier => {
  if (score >= HAT_HIGH_INTEREST_THRESHOLD) return "high";
  if (score <= HAT_LOW_INTEREST_THRESHOLD) return "low";
  return "some";
};

export const TIER_LABEL: Record<InterestTier, string> = {
  high: "High Interest",
  some: "Some Interest",
  low: "Low Interest",
};

export const TIER_LABEL_COLOR: Record<InterestTier, string> = {
  high: "#7a4100",
  some: "#b76e00",
  low: "#919eab",
};

export const getTileBackground = (tier: InterestTier, color?: string) => {
  if (tier === "low") return "#ffffff";
  const base = color || "#078DEE";
  const overlay =
    tier === "high" ? "rgba(255,255,255,0.75)" : "rgba(255,255,255,0.9)";
  return `linear-gradient(${overlay}, ${overlay}), ${base}`;
};

export interface HatCoreEntry {
  key: string;
  score: number;
  tier: InterestTier;
  facet?: FacetDefinition;
  /** 0–1, the height of the tile's interest bar. */
  ratio: number;
  /** The climber's own tier for this type agrees with the hat's. */
  isMatch: boolean;
}

export interface HatWorkValueEntry {
  key: string;
  score: number;
  facet?: FacetDefinition;
  isMatch: boolean;
}

export interface HatFitInput {
  /**
   * Optional because a caller may render before the hat's content has
   * resolved. The hat card never did — it only mounts once the hat is there —
   * but the Climb Plan review renders its panels immediately, and reading
   * `riasecScores` off an undefined hat crashed the whole screen behind the
   * error boundary rather than leaving one panel blank.
   */
  hat?: MSHatData | null;
  cmsFacets?: FacetDefinition[];
  userHighInterestRiasec?: string[];
  userLowInterestRiasec?: string[];
  userSomeInterestRiasec?: string[];
  userTopWorkValues?: string[];
}

/**
 * The hat's six Core types in canonical order, and its top two Work Values.
 *
 * A core type only counts as a match when the hat's own score is valid —
 * below `HAT_MIN_VALID_SCORE` means the O*NET figure is missing, not that the
 * hat scores low, and a Fit tag on missing data would be a claim nobody made.
 */
export const deriveHatFit = ({
  hat,
  cmsFacets = [],
  userHighInterestRiasec,
  userLowInterestRiasec,
  userSomeInterestRiasec,
  userTopWorkValues,
}: HatFitInput): {
  coreEntries: HatCoreEntry[];
  workValueEntries: HatWorkValueEntry[];
} => {
  const facetMap = new Map(cmsFacets.map((f) => [f.facetCode, f]));
  const userHighSet = new Set(userHighInterestRiasec || []);
  const userLowSet = new Set(userLowInterestRiasec || []);
  const userSomeSet = new Set(userSomeInterestRiasec || []);
  const userWorkValueSet = new Set(userTopWorkValues || []);

  const coreEntries = RIASEC_KEYS.map((key) => {
    const score = hat?.riasecScores?.[key] || 0;
    const tier = getTier(score);
    const facet = facetMap.get(RIASEC_SCORE_TO_FACET[key]);
    const ratio = Math.max(0, Math.min(1, score / 7));
    const isMatch =
      score >= HAT_MIN_VALID_SCORE &&
      ((tier === "high" && userHighSet.has(key)) ||
        (tier === "low" && userLowSet.has(key)) ||
        (tier === "some" && userSomeSet.has(key)));
    return { key, score, tier, facet, ratio, isMatch };
  });

  const workValueEntries = WORK_VALUE_KEYS.map((key) => ({
    key,
    score: hat?.workValues?.[key] || 0,
    facet: facetMap.get(WORK_VALUE_SCORE_TO_FACET[key]),
  }))
    .filter((e) => e.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map((e) => ({ ...e, isMatch: userWorkValueSet.has(e.key) }));

  return { coreEntries, workValueEntries };
};

// --- The fit message ---

/**
 * Points earned when the hat's interest tier for a core type meets the
 * climber's tier for that same type. Outer key = hat tier, inner key = user
 * tier. Exact-tier matches score positive (high 3, low 2, some 1); a one-step
 * tier gap is neutral; the full high<->low gap is penalised. (Symmetric.)
 */
const CORE_TIER_MATCH_POINTS: Record<
  InterestTier,
  Record<InterestTier, number>
> = {
  high: { high: 3, some: 0, low: -1 },
  some: { high: 0, some: 1, low: 0 },
  low: { high: -1, some: 0, low: 2 },
};

export type FitCategoryKey = "mustTry" | "mightLike" | "mixed" | "mightNotLike";

/**
 * The four fit tags in descending order of fit score. All share the purple
 * sparkle chip; only the label and the trailing sentence change. `tone` is the
 * sentence after "You have N fits for <Hat>." - mightNotLike has a bespoke
 * sentence instead, so its tone is null.
 */
export const FIT_CATEGORY: Record<
  FitCategoryKey,
  { label: string; tone: string | null }
> = {
  mustTry: {
    label: "Must Try",
    tone:
      "Strong signals point to a real fit here \u2014 explore the details of this " +
      "Hat to confirm what you're seeing.",
  },
  mightLike: {
    label: "Might Like",
    tone:
      "There are some great signs of a fit here \u2014 explore the details of this " +
      "Hat to see what catches your eye!",
  },
  mixed: {
    label: "Mixed Signals",
    tone:
      "You might find a few things you like and a few things you don't \u2014 " +
      "explore the details to see what you think.",
  },
  mightNotLike: { label: "Might Not Like", tone: null },
};

interface FitThresholds {
  mustTry: number;
  mightLike: number;
  mixed: number;
}

/** The highest tag the weighted fit score clears. */
const fitCategoryOf = (
  fitScore: number,
  thresholds: FitThresholds,
): FitCategoryKey => {
  if (fitScore >= thresholds.mustTry) return "mustTry";
  if (fitScore >= thresholds.mightLike) return "mightLike";
  if (fitScore >= thresholds.mixed) return "mixed";
  return "mightNotLike";
};

export interface HatFitMessage {
  /** The chip's wording: "Must Try", "Might Like", ... */
  label: string;
  categoryKey: FitCategoryKey;
  /** Matched core types plus matched work values - the purple Fit tags. */
  fitCount: number;
  /** "4 fits" / "1 fit", for a caller that emphasises it on its own. */
  fitLabel: string;
  /** The sentence after the count, or null for mightNotLike. */
  tone: string | null;
  /** The whole thing as one flat sentence, for a surface with no rich text. */
  message: string;
}

/**
 * How well this hat fits this climber, as the hat card says it out loud.
 *
 * SHARED ON PURPOSE. The card renders the sentence in pieces so it can
 * emphasise the count and the hat's name; a PDF has no spans and takes
 * `message`. Both read the same score, because two copies of this arithmetic
 * would be two answers to "does this hat fit me".
 *
 * The weighted fit score is NOT `fitCount`: each core type with a valid hat
 * score and a known user tier contributes per `CORE_TIER_MATCH_POINTS`, and
 * each matched top work value adds one point, capped at two.
 */
export const deriveHatFitMessage = (
  input: HatFitInput & { hatName?: string },
): HatFitMessage => {
  const { coreEntries, workValueEntries } = deriveHatFit(input);

  const userHighSet = new Set(input.userHighInterestRiasec || []);
  const userLowSet = new Set(input.userLowInterestRiasec || []);
  const userSomeSet = new Set(input.userSomeInterestRiasec || []);
  const userWorkValueSet = new Set(input.userTopWorkValues || []);

  const userTierOf = (key: string): InterestTier | null => {
    if (userHighSet.has(key)) return "high";
    if (userSomeSet.has(key)) return "some";
    if (userLowSet.has(key)) return "low";
    return null;
  };

  const coreScore = coreEntries
    .filter((entry) => entry.score >= HAT_MIN_VALID_SCORE)
    .reduce((sum, entry) => {
      const userTier = userTierOf(entry.key);
      return userTier
        ? sum + CORE_TIER_MATCH_POINTS[entry.tier][userTier]
        : sum;
    }, 0);

  const workValueMatches = workValueEntries.filter((e) => e.isMatch).length;
  const fitScore = coreScore + Math.min(workValueMatches, 2);

  // The bar is lower for a climber who has not finished Work Values, because
  // two of the available points are out of their reach.
  const thresholds =
    userWorkValueSet.size > 0
      ? { mustTry: 7, mightLike: 4, mixed: 1 }
      : { mustTry: 6, mightLike: 3, mixed: 1 };

  const categoryKey = fitCategoryOf(fitScore, thresholds);

  const category = FIT_CATEGORY[categoryKey];
  const fitCount =
    coreEntries.filter((e) => e.isMatch).length + workValueMatches;
  const fitLabel = `${fitCount} fit${fitCount === 1 ? "" : "s"}`;
  const hatName = input.hatName || input.hat?.displayName || "this Hat";

  const message =
    categoryKey === "mightNotLike"
      ? `You have ${fitLabel}. ${hatName} may not be a fit for you \u2013 but ` +
        "explore the details of this Hat to know for sure."
      : `You have ${fitLabel} for ${hatName}. ${category.tone}`;

  return {
    label: category.label,
    categoryKey,
    fitCount,
    fitLabel,
    tone: category.tone,
    message,
  };
};

// --- The tiles ---

const CoreList = styled(Box, {
  label: "BackCenterCoreList",
})(() => ({
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  justifyContent: "center",
  width: "100%",
}));

const CoreTile = styled(Box, {
  label: "BackCenterCoreTile",
  shouldForwardProp: (prop) =>
    prop !== "tier" && prop !== "facetColor" && prop !== "matched",
})<{ tier: InterestTier; facetColor?: string; matched?: boolean }>(
  ({ tier, facetColor, matched }) => ({
    position: "relative",
    width: 148,
    height: 54,
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 8px",
    borderRadius: 8,
    cursor: "pointer",
    background: getTileBackground(tier, facetColor),
    ...(matched && {
      outline: "2px solid #8c31fc",
      outlineOffset: "-2px",
    }),
  }),
);

const TileDarken = styled(Box, {
  label: "BackCenterTileDarken",
})(() => ({
  position: "absolute",
  inset: 0,
  borderRadius: 8,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  pointerEvents: "none",
}));

const FitBadge = styled(Box, {
  label: "BackCenterFitBadge",
})(() => ({
  position: "absolute",
  top: -9,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  gap: 3,
  height: 17,
  padding: "2px 7px",
  borderRadius: 999,
  backgroundColor: "#8c31fc",
  zIndex: 2,
}));

const FitBadgeText = styled("span", {
  label: "BackCenterFitBadgeText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "9px",
  lineHeight: "12px",
  letterSpacing: "0.18px",
  color: "#ffffff",
}));

/** Purple "Fit" pill anchored to the top edge of a matched tile. */
export const FitTag = () => (
  <FitBadge>
    <SparkleGlyph />
    <FitBadgeText>Fit</FitBadgeText>
  </FitBadge>
);

const TileType = styled(Box, {
  label: "BackCenterTileType",
})(() => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 4,
}));

const TileName = styled(Typography, {
  label: "BackCenterTileName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#1c252e",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const TileInterest = styled(Typography, {
  label: "BackCenterTileInterest",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "10px",
  lineHeight: "12px",
  color: TIER_LABEL_COLOR[tier],
}));

const BarTrack = styled(Box, {
  label: "BackCenterBarTrack",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  width: 6,
  height: 42,
  flexShrink: 0,
  borderRadius: 50,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  backgroundColor:
    tier === "low" ? "rgba(145,158,171,0.16)" : "rgba(255,171,0,0.16)",
}));

const BarFill = styled(Box, {
  label: "BackCenterBarFill",
  shouldForwardProp: (prop) => prop !== "tier",
})<{ tier: InterestTier }>(({ tier }) => ({
  width: "100%",
  borderRadius: 50,
  backgroundColor: tier === "low" ? "#c4cdd5" : "#ffab00",
}));

const WorkValuesRow = styled(Box, {
  label: "BackCenterWorkValuesRow",
})(() => ({
  display: "flex",
  gap: 24,
  justifyContent: "center",
  width: "100%",
}));

const WorkValueItem = styled(Box, {
  label: "BackCenterWorkValueItem",
  shouldForwardProp: (prop) => prop !== "matched",
})<{ matched?: boolean }>(({ matched }) => ({
  position: "relative",
  display: "flex",
  alignItems: "center",
  gap: 8,
  boxSizing: "border-box",
  padding: "2px 8px 2px 2px",
  borderRadius: 8,
  cursor: "pointer",
  border: `2px solid ${matched ? "#8c31fc" : "transparent"}`,
}));

const WorkValueIcon = styled(Box, {
  label: "BackCenterWorkValueIcon",
})(() => ({
  width: 40,
  height: 40,
  borderRadius: "100%",
  backgroundColor: "#c684ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& img": {
    width: 40,
    height: 40,
    objectFit: "contain",
    borderRadius: "100%",
  },
}));

const WorkValueName = styled(Typography, {
  label: "BackCenterWorkValueName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#ffffff",
}));

const WorkValueDarken = styled(Box, {
  label: "BackCenterWorkValueDarken",
})(() => ({
  position: "absolute",
  inset: -2,
  borderRadius: 8,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  pointerEvents: "none",
}));

interface HatCoreTilesProps {
  entries: HatCoreEntry[];
  /** Before the fit is revealed there are no Fit tags and nothing is dimmed. */
  showFit: boolean;
  onSelectFacet?: (facet?: FacetDefinition) => void;
  /** Overrides the wrapping list's own layout — the review needs two columns. */
  sx?: React.ComponentProps<typeof Box>["sx"];
}

export const HatCoreTiles = ({
  entries,
  showFit,
  onSelectFacet,
  sx,
}: HatCoreTilesProps) => (
  <CoreList sx={sx}>
    {entries.map((entry) => {
      const label = entry.facet?.variable || entry.key;
      const darken = showFit && !entry.isMatch;
      return (
        <CoreTile
          key={entry.key}
          tier={entry.tier}
          facetColor={entry.facet?.color}
          matched={showFit && entry.isMatch}
          onClick={() => onSelectFacet?.(entry.facet)}
          role="button"
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelectFacet?.(entry.facet);
            }
          }}
        >
          {showFit && entry.isMatch && <FitTag />}
          {entry.facet?.icon ? (
            <img
              src={entry.facet.icon}
              alt={label}
              width={40}
              height={40}
              style={{ flexShrink: 0 }}
            />
          ) : (
            <Box sx={{ width: 40, flexShrink: 0 }} />
          )}
          <TileType>
            <TileName>{entry.facet?.longLabel || label}</TileName>
            <TileInterest tier={entry.tier}>
              {TIER_LABEL[entry.tier]}
            </TileInterest>
          </TileType>
          <BarTrack tier={entry.tier}>
            <BarFill
              tier={entry.tier}
              style={{ height: `${Math.round(entry.ratio * 100)}%` }}
            />
          </BarTrack>
          {darken && <TileDarken />}
        </CoreTile>
      );
    })}
  </CoreList>
);

interface HatWorkValuesProps {
  entries: HatWorkValueEntry[];
  showFit: boolean;
  onSelectFacet?: (facet?: FacetDefinition) => void;
  sx?: React.ComponentProps<typeof Box>["sx"];
}

export const HatWorkValues = ({
  entries,
  showFit,
  onSelectFacet,
  sx,
}: HatWorkValuesProps) => (
  <WorkValuesRow sx={sx}>
    {entries.map((wv) => {
      const label = wv.facet?.variable || wv.facet?.longLabel || wv.key;
      const darken = showFit && !wv.isMatch;
      return (
        <WorkValueItem
          key={wv.key}
          matched={showFit && wv.isMatch}
          onClick={() => onSelectFacet?.(wv.facet)}
          role="button"
          tabIndex={0}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSelectFacet?.(wv.facet);
            }
          }}
        >
          {showFit && wv.isMatch && <FitTag />}
          <WorkValueIcon>
            {wv.facet?.icon ? (
              <img src={wv.facet.icon} alt={label} />
            ) : (
              <Typography sx={{ fontSize: 16, color: "#fff" }}>
                {label.charAt(0).toUpperCase()}
              </Typography>
            )}
          </WorkValueIcon>
          <WorkValueName>{label}</WorkValueName>
          {darken && <WorkValueDarken />}
        </WorkValueItem>
      );
    })}
  </WorkValuesRow>
);
